import React from 'react';
import { Modal, View } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import { dispatchService } from '../../../services/central-api/dispatch';
import ActingBodiesDropdown from './ActingBodiesDropdown';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import moment from 'moment';
import TagsDropdown from './TagsDropdown';
import MyAppText from '../../../components/MyAppText';
import GenericUserDropdown from '../../../components/Dropdowns/GenericUserDropdown';
import { ScrollView } from 'react-native-web-hover';
import ActingBodiesCommandsDropdown from '../../../components/Dropdowns/ActingBodiesCommandsDropdown';
import TypeDropdown from '../Occurrences/TypeDropdown';
import OccurrenceTypesDropdown from '../../../components/dispatch/OccurrenceTypesDropdown';
import MyDateRangePicker from '../../../components/MyDateRangePicker';
import { useForm } from 'react-hook-form';
import FileFormatDropdown from '../../../components/FileFormatDropdown';
import { useAuth } from '../../../context/Auth';

interface ModalProps {
    isModalVisible: boolean;
    onClose: () => void;
}

type ValuePiece = Date | null;
const regex = /^[a-zA-Z0-9 +\-./:=_ãõẽíáàâêóôúüçÃÕẼÍÁÀÂÊÓÔÚÜÇ]*$/;

interface ReportModalForm {
    period: { from: ValuePiece; to: ValuePiece; };
    name: string;
    reportFormat: 'csv' | 'xlsx' | 'pdf';
    tags: number[];
    actingBodyId: number;
    assumedBy: number;
    receivedBy: number;
    transferredBy: number;
    finishedBy: number;
    actingBodyCommandIds: number[];
    triggerTypes: (TriggerType | 'manual')[];
    occurrenceTypes: number[];
}

export default function ReportModal({ onClose, isModalVisible }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const windowInfo = WindowInformation();

    const defaultEmptyValues: ReportModalForm = {
        period: { from: new Date(), to: new Date() },
        name: '',
        reportFormat: 'xlsx',
        tags: [],
        actingBodyId: 0,
        assumedBy: 0,
        receivedBy: 0,
        transferredBy: 0,
        finishedBy: 0,
        actingBodyCommandIds: [],
        triggerTypes: [],
        occurrenceTypes: []
    };

    const form = useForm<ReportModalForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    async function formSubmit() {
        try {
            const values = form.getValues();

            values.period.to!.setHours(23, 59, 59);
            values.period.from!.setHours(0, 0, 0);

            await dispatchService.createReport({
                name: values.name,
                isPdf: values.reportFormat == 'pdf',
                isCsv: values.reportFormat == 'csv',
                startDate: moment(values.period.from).format('YYYY-MM-DD'),
                endDate: moment(values.period.to).format('YYYY-MM-DD'),
                tags: values.tags,
                actingBodyId: values.actingBodyId ? values.actingBodyId : undefined,
                assumedBy: values.assumedBy ? values.assumedBy : undefined,
                transferredBy: values.transferredBy ? values.transferredBy : undefined,
                receivedBy: values.receivedBy ? values.receivedBy : undefined,
                finishedBy: values.finishedBy ? values.finishedBy : undefined,
                actingBodyCommandIds: values.actingBodyCommandIds,
                occurrenceTypesIds: values.occurrenceTypes,
                triggerTypeIds: values.triggerTypes
            });

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            onClose();
            form.reset(defaultEmptyValues);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function getActingBodyId() {
        if (!authenticatedUser || !authenticatedUser.actingBodyUnit.actingBodyId) {
            return 0;
        }

        if (authenticatedUser.isAdmin || authenticatedUser.permissions.dispatch_manage_all_acting_bodies_reports) {
            if (form.getValues().actingBodyId) {
                return form.getValues().actingBodyId;
            }
            return 0;
        } else {
            return authenticatedUser.actingBodyUnit.actingBodyId;
        }
    }

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                onClose();
                form.reset(defaultEmptyValues);
            }}
        >
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: '40%' }]}>
                    <ScrollView contentContainerStyle={styles.scrollContent}>
                        <MyAppText style={styles.textHeader}>{translate('occurrenceReport')}</MyAppText>
                        <MyAppText style={{ color: theme.colors.textColor, fontSize: 12 }}>{translate('occurrenceReportInformation')}</MyAppText>
                        {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_reports) &&
                            <ActingBodiesDropdown
                                includeAllOption={false}
                                form={form}
                                fieldName='actingBodyId'
                                required={(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_reports)}
                                zIndex={11}
                            />
                        }
                        <FormInput
                            label={translate('name')}
                            placeholder={translate('name')}
                            form={form}
                            fieldName='name'
                            required={true}
                            customValidations={{
                                validName: (val: string) => regex.test(val) || translate('invalidName')
                            }}
                        />
                        <FileFormatDropdown
                            form={form}
                            fieldName='reportFormat'
                            zIndex={10}
                            required={true}
                            includesXlsx={true}
                        />
                        <MyDateRangePicker
                            label={translate('period')}
                            maxDate={moment().toDate()}
                            form={form}
                            fieldName='period'
                            zIndex={9}
                            required={true}
                        />
                        <TypeDropdown
                            form={form}
                            fieldName='triggerTypes'
                            zIndex={8}
                        />
                        <OccurrenceTypesDropdown
                            form={form}
                            fieldName='occurrenceTypes'
                            zIndex={7}
                            actingBodyId={getActingBodyId()}
                        />
                        <ActingBodiesCommandsDropdown
                            form={form}
                            fieldName='actingBodyCommandIds'
                            actingBodyId={getActingBodyId()}
                            zIndex={6}
                        />
                        <GenericUserDropdown form={form} fieldName='assumedBy' actingBodyId={getActingBodyId()} zIndex={5} label='assumedBy' />
                        <GenericUserDropdown form={form} fieldName='transferredBy' actingBodyId={getActingBodyId()} zIndex={4} label='transferredBy' />
                        <GenericUserDropdown form={form} fieldName='receivedBy' actingBodyId={getActingBodyId()} zIndex={3} label='receivedBy' />
                        <GenericUserDropdown form={form} fieldName='finishedBy' actingBodyId={getActingBodyId()} zIndex={2} label='finishedBy' />
                        <TagsDropdown
                            actingBodyId={getActingBodyId()}
                            form={form}
                            fieldName='tags'
                            zIndex={1}
                        />
                    </ScrollView>
                    <FormActions
                        onSubmit={async () => {
                            await formSubmit();
                        }}
                        onClose={() => {
                            onClose();
                            form.reset(defaultEmptyValues);
                        }}
                        disabled={!form.formState.isValid}
                        confirmText={'download'} />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 15,
        maxHeight: 690
    },
    scrollContent: {
        rowGap: 15,
        paddingRight: 10
    },
    tagsLabel: {
        color: theme.colors.labelColor,
        fontSize: 16,
    },
    textHeader: {
        fontSize: 22,
        fontWeight: 'bold',
        color: theme.colors.textColor
    }
}));
